import React, { useState, useEffect, useRef } from 'react'
import { Plus, FileText, CheckCircle, Clock, Archive, TrendingUp } from 'lucide-react'
import DataTable from '../../../components/DataTable/DataTable'
import Breadcrumb from '../../../components/Common/Breadcrumb'
import ConfirmationModal from '../../../components/ConfirmationModal/ConfirmationModal'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import projectDocumentService from '../../../services/projectDocumentService'
import '../../../components/ConfirmationModal/ConfirmationModal.css'

const STATUS_COLORS = {
  Draft: { bg: '#5B647820', color: '#5B6478', border: '#5B647840' },
  'In Review': { bg: '#6A4CA220', color: '#51418F', border: '#6A4CA240' },
  Approved: { bg: '#10B98120', color: '#059669', border: '#10B98140' },
  Obsolete: { bg: '#EF444420', color: '#DC2626', border: '#EF444440' }
}

const ProjectDocumentList = () => {
  const navigate = useNavigate()
  const [selectedRows, setSelectedRows] = useState([])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({ total: 0, draft: 0, inReview: 0, approved: 0, obsolete: 0 })
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [deleteItem, setDeleteItem] = useState(null)
  const hasLoadedRef = useRef(false)
  const hasShownEmptyToastRef = useRef(false)

  useEffect(() => {
    if (!hasLoadedRef.current) {
      hasLoadedRef.current = true
      loadData()
    }
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listRes, statsRes] = await Promise.all([
        projectDocumentService.getAll({ limit: 1000 }),
        projectDocumentService.getStats()
      ])

      if (listRes?.success) {
        const documents = listRes.documents || []
        setData(documents.map((d) => ({ ...d, id: d._id })))
        if (documents.length === 0 && !hasShownEmptyToastRef.current) {
          hasShownEmptyToastRef.current = true
          toast.info('No project documents found. Upload your first document to get started!')
        }
      } else {
        toast.error(listRes?.message || 'Failed to load documents')
      }

      if (statsRes?.success) {
        setStats(statsRes.data || {})
      }
    } catch (error) {
      console.error('Error loading project documents:', error)
      toast.error('Failed to load project documents')
    } finally {
      setLoading(false)
    }
  }

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
  }

  const formatFileSize = (bytes) => {
    if (!bytes) return '-'
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const columns = [
    {
      key: 'documentNumber',
      label: 'Doc #',
      sortable: true,
      render: (value) => <span className="font-medium text-gray-900">{value || 'N/A'}</span>
    },
    {
      key: 'documentName',
      label: 'Document Name',
      sortable: true,
      render: (value) => (
        <div style={{ maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {value || 'N/A'}
        </div>
      )
    },
    {
      key: 'project',
      label: 'Project',
      sortable: true,
      render: (_, row) => (
        <span>{row.project?.projectName || row.projectName || 'N/A'}</span>
      )
    },
    {
      key: 'category',
      label: 'Category',
      sortable: true,
      render: (value) => value || 'Other'
    },
    {
      key: 'version',
      label: 'Version',
      sortable: true,
      render: (value) => value || '1.0'
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const status = value || 'Draft'
        const c = STATUS_COLORS[status] || STATUS_COLORS.Draft
        return (
          <span
            className="inline-flex items-center px-2.5 py-0.5 text-xs font-medium"
            style={{ backgroundColor: c.bg, color: c.color, border: `1px solid ${c.border}` }}
          >
            {status}
          </span>
        )
      }
    },
    {
      key: 'fileName',
      label: 'File',
      sortable: true,
      render: (value, row) => (
        <div>
          <div>{value || '-'}</div>
          <div style={{ fontSize: '11px', color: 'var(--color-text-secondary, #5B6478)' }}>
            {formatFileSize(row.fileSize)}
          </div>
        </div>
      )
    },
    {
      key: 'updatedAt',
      label: 'Updated',
      sortable: true,
      render: (value) => formatDate(value)
    }
  ]

  const breadcrumbItems = [
    { label: 'Projects', path: '/projects', onClick: () => navigate('/projects') },
    { label: 'Transactions', path: '/projects?tab=transactions', onClick: () => navigate('/projects?tab=transactions') },
    { label: 'Project Documents', path: '/projects/documents', isCurrent: true }
  ]

  return (
    <div className="sales-dashboard">
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="page-header">
        <div className="page-title">
          <h1>Project Documents</h1>
          <p>Document management and version control for projects</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={() => navigate('/projects/documents/new')}>
            <Plus size={16} />
            Add Document
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><FileText size={16} /></div>
            <div className="metric-trend positive"><TrendingUp size={12} /><span>All</span></div>
          </div>
          <div className="metric-value">{stats.total || 0}</div>
          <div className="metric-title">Total Documents</div>
          <div className="metric-subtitle">Active records</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><CheckCircle size={16} /></div>
            <div className="metric-trend positive"><TrendingUp size={12} /><span>Live</span></div>
          </div>
          <div className="metric-value">{stats.approved || 0}</div>
          <div className="metric-title">Approved</div>
          <div className="metric-subtitle">Published documents</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><Clock size={16} /></div>
            <div className="metric-trend neutral"><TrendingUp size={12} /><span>Review</span></div>
          </div>
          <div className="metric-value">{stats.inReview || 0}</div>
          <div className="metric-title">In Review</div>
          <div className="metric-subtitle">Pending approval</div>
        </div>
        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon"><Archive size={16} /></div>
            <div className="metric-trend negative"><TrendingUp size={12} /><span>Draft</span></div>
          </div>
          <div className="metric-value">{stats.draft || 0}</div>
          <div className="metric-title">Draft</div>
          <div className="metric-subtitle">{stats.obsolete || 0} obsolete</div>
        </div>
      </div>

      <div className="dashboard-content">
        <DataTable
          data={data}
          columns={columns}
          loading={loading}
          onEdit={(row) => navigate(`/projects/documents/edit/${row._id || row.id}`)}
          onView={(row) => navigate(`/projects/documents/view/${row._id || row.id}`)}
          onDelete={(row) => {
            setDeleteItem(row)
            setShowDeleteModal(true)
          }}
          selectedRows={selectedRows}
          onSelectionChange={setSelectedRows}
          searchPlaceholder="Search documents..."
          emptyMessage="No project documents found."
          noCard={true}
        />
      </div>

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={async () => {
          if (!deleteItem) return
          try {
            const res = await projectDocumentService.remove(deleteItem._id || deleteItem.id)
            if (res.success) {
              toast.success('Document deleted')
              loadData()
            } else {
              toast.error(res.message || 'Delete failed')
            }
          } catch {
            toast.error('Failed to delete document')
          } finally {
            setShowDeleteModal(false)
            setDeleteItem(null)
          }
        }}
        title="Delete Document"
        message={`Delete "${deleteItem?.documentName || deleteItem?.documentNumber || ''}"? This cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default ProjectDocumentList
